import React, { useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import { utils } from 'utils';
import {
  Button,
  Heading,
  Stack,
  Form,
  createSchema,
} from '@guestyci/arc';
import { useBreakpoint } from '../../../utils/useBreakpoint';
import { extractOktaRuleMap } from '../../utils/parseOktaRequirements';
import { getSessionEmail, getSessionFirstName, getSessionLastName } from '../../utils/widgetConfig';
import { buildPasswordSchema } from '../../utils/passwordSchema';
import PasswordInput from './PasswordInput';
import ValidationRules from './ValidationRules';
import { FormLayout } from '../../layout';

const createPasswordFormSchema = (email, firstName, lastName, ruleMap) =>
  createSchema({ password: buildPasswordSchema(email, firstName, lastName, ruleMap) });

const CustomResetPassword = ({
  onCreatePassword,
  error: externalError,
  isSubmitting,
  oktaRequirements = [],
}) => {
  const isDesktop = useBreakpoint('lg');
  const logEvent = utils.eventsLogger?.unifiedLoginDio?.track;
  const isOnboardingFlow = sessionStorage.getItem('isOnboardingFlow');

  const email = getSessionEmail();
  const firstName = getSessionFirstName();
  const lastName = getSessionLastName();

  const ruleMapRef = useRef(extractOktaRuleMap(oktaRequirements));

  useEffect(() => {
    ruleMapRef.current = extractOktaRuleMap(oktaRequirements);
  }, [oktaRequirements]);

  const dynamicResolver = useCallback(
    (values, context, options) =>
      zodResolver(createPasswordFormSchema(email, firstName, lastName, ruleMapRef.current))(values, context, options),
    [email, firstName, lastName]
  );

  const form = useForm({
    resolver: dynamicResolver,
    defaultValues: {
      password: '',
    },
    mode: 'onBlur',
  });

  const password = form.watch('password');
  const submitted = form.formState.isSubmitted;

  const logCreatePasswordEvent = (submitted) => {
    if (isOnboardingFlow && logEvent) {
      if (submitted === false) {
        logEvent('registration_Choose_password', 'submit', {
          properties: 'create_password/error',
        });
      } else {
        logEvent('registration_Choose_password', 'submit', {
          properties: 'create_password_signup',
        });
      }
    }
  };

  const handleSubmit = (data) => {
    const result = onCreatePassword(data.password);
    logCreatePasswordEvent(result);
  };

  return (
    <FormLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="gst-flex gst-flex-col gst-flex-1">
          <Stack spacing={6} justify="between" className="gst-flex-1 gst-pt-6 lg:gst-pt-0">
            <Stack spacing={6}>
              <Heading variant={isDesktop ? 'h1' : 'h2'}>
                {t('Create your password')}
              </Heading>
              <PasswordInput
                form={form}
                isSubmitting={isSubmitting}
                externalError={externalError}
              />
              <ValidationRules
                password={password}
                email={email}
                firstName={firstName}
                lastName={lastName}
                submitted={submitted}
                oktaRequirements={oktaRequirements}
              />
            </Stack>

            <Button
              type="submit"
              className="gst-w-full lg:gst-w-auto"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {t('Create password')}
            </Button>
          </Stack>
        </form>
      </Form>
    </FormLayout>
  );
};

export default CustomResetPassword;
